import type { FidelityLevel, HashInfo, JobRecord } from '../core/types';
import {
  DEFAULT_MAX_RECORDS,
  THUMBNAIL_MAX_BYTES,
  clearJobs,
  countJobs,
  deleteJob,
  findLatestByHash,
  listJobs,
  openHistoryDb,
  pruneJobs,
  putJob,
  type HistoryDatabase,
  type JobQuery,
} from './history-db';

export interface SaveJobInput {
  sourceName: string;
  sourceFormat: string;
  targetFormat: string;
  sourceSize: number;
  outputSize: number;
  engine: string;
  mode?: JobRecord['mode'];
  /** 本次实际消耗的积分（服务端记账返回值；免费次数记 0）。 */
  cost?: number;
  status: JobRecord['status'];
  fidelityLevel: FidelityLevel;
  fidelitySummary: string;
  durationMs: number;
  hash: HashInfo;
  errorCode?: string;
  /** 缩略图 data URL；超过 200KB 会被丢弃。 */
  thumbnailDataUrl?: string;
}

export interface HistoryRepository {
  save(input: SaveJobInput): Promise<JobRecord>;
  list(query?: JobQuery): Promise<JobRecord[]>;
  count(query?: JobQuery): Promise<number>;
  remove(id: string): Promise<void>;
  clear(): Promise<void>;
  prune(maxRecords?: number): Promise<number>;
}

export interface HistoryRepositoryOptions {
  dbName?: string;
  maxRecords?: number;
  /** 允许注入数据库（测试用），缺省时懒加载真实 IndexedDB。 */
  database?: Promise<HistoryDatabase>;
}

let sequence = 0;

function newJobId(): string {
  sequence += 1;
  const suffix =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
      : Date.now().toString(36);
  return `job_${Date.now().toString(36)}_${sequence}_${suffix}`;
}

/**
 * 记录仓：写入前去重（同源哈希 + 同目标格式复用缩略图），写入后按上限清理。
 */
export function createHistoryRepository(options: HistoryRepositoryOptions = {}): HistoryRepository {
  const maxRecords = options.maxRecords ?? DEFAULT_MAX_RECORDS;
  const database = options.database ?? openHistoryDb(options.dbName);

  return {
    async save(input) {
      const db = await database;
      const existing = await findLatestByHash(db, input.hash.value, input.targetFormat);
      const thumbnail = pickThumbnail(input.thumbnailDataUrl) ?? existing?.thumbnailDataUrl;

      const record: JobRecord = {
        id: newJobId(),
        createdAt: Date.now(),
        sourceName: input.sourceName,
        sourceFormat: input.sourceFormat,
        targetFormat: input.targetFormat,
        sourceSize: input.sourceSize,
        outputSize: input.outputSize,
        engine: input.engine,
        mode: input.mode ?? 'local',
        ...(input.cost === undefined ? {} : { cost: input.cost }),
        fidelityLevel: input.fidelityLevel,
        fidelitySummary: input.fidelitySummary,
        status: input.status,
        errorCode: input.errorCode,
        durationMs: Math.max(0, Math.round(input.durationMs)),
        sourceHash: input.hash.value,
        sourceHashPartial: input.hash.partial,
        thumbnailDataUrl: thumbnail,
        duplicateOf: existing?.id,
      };

      await putJob(db, record);
      await pruneJobs(db, maxRecords);
      return record;
    },

    async list(query) {
      return listJobs(await database, query);
    },

    async count(query) {
      return countJobs(await database, query);
    },

    async remove(id) {
      await deleteJob(await database, id);
    },

    async clear() {
      await clearJobs(await database);
    },

    async prune(limit = maxRecords) {
      return pruneJobs(await database, limit);
    },
  };
}

function pickThumbnail(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (!value.startsWith('data:image/')) return undefined;
  if (value.length > THUMBNAIL_MAX_BYTES) return undefined;
  return value;
}
